import React from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import type { Review } from '@vibesites/schema';
import { useCollectionBinding, useEditMode, usePropertyField, useSlotGroup, useSlotGroupOverrides } from '@vibesites/runtime/react';
import { EditableSlotGroup } from '@vibesites/runtime/react';
import { RichTextContent } from '@vibesites/runtime/react';
import { useScrollReveal, useStaggerReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';
import { toGoogleWriteReviewUrl } from '../utils/googleReviewUrl';
import { GlowTrace } from './GlowTrace';

function hasValue(val: string | null | undefined): val is string {
  if (!val) return false;
  const normalized = val.trim().toLowerCase();
  return normalized !== '' && normalized !== 'false' && normalized !== 'null' && normalized !== 'undefined';
}

function abbreviateName(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length <= 1) return parts[0] || name;
  return `${parts[0]} ${parts[parts.length - 1][0]}.`;
}

const ctaLinkDef: SlotGroupDefinition = {
  label: 'Reviews Page Link',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultValue: '/reviews',
    },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Read All Reviews' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const googleCtaLinkDef: SlotGroupDefinition = {
  label: 'Google Review Link',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_google_business_url',
      suggestedFields: ['property_google_business_url', 'property_google_place_url'],
    },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Leave a Google Review' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

function StarIcon({ filled, className }: { filled: boolean; className?: string }) {
  return (
    <svg
      className={className}
      fill={filled ? 'currentColor' : 'none'}
      stroke="currentColor"
      strokeWidth={filled ? 0 : 1.5}
      viewBox="0 0 20 20"
    >
      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
    </svg>
  );
}

const ctaButtonClasses =
  'inline-flex w-full items-center justify-center whitespace-nowrap rounded-full px-8 py-3 text-center text-sm font-semibold tracking-widest uppercase border-2 border-[var(--vibe-color-text-on-primary)] text-[var(--vibe-color-text-on-primary)] hover:bg-[var(--vibe-color-text-on-primary)] hover:text-[var(--vibe-color-primary)] hover:-translate-y-0.5 hover:shadow-lg transition-all duration-300';

export function ReviewsCallout() {
  const ctaLink = useSlotGroup('reviewscallout.cta_link', ctaLinkDef);
  const googleCtaLink = useSlotGroup('reviewscallout.google_cta_link', googleCtaLinkDef);
  const googlePlaceUrl = usePropertyField('property_google_place_url');
  const googleReviewHref = toGoogleWriteReviewUrl(googleCtaLink.url, googlePlaceUrl);
  const slotGroupOverrides = useSlotGroupOverrides();

  const rawReviews = useCollectionBinding<Review>('reviewscallout.items', {
    collection: 'reviews',
  });

  const editMode = useEditMode();
  const isEditMode = editMode?.isEditMode ?? false;

  const reviews = rawReviews
    ? [...rawReviews].sort((a, b) => {
        const da = a.date ? new Date(a.date).getTime() : 0;
        const db = b.date ? new Date(b.date).getTime() : 0;
        return db - da;
      })
    : null;

  const displayReviews = reviews && reviews.length > 0 ? reviews.slice(0, 3) : [];
  const showGoogleCta = hasValue(googleCtaLink.url) || isEditMode;

  useRevealStyles();
  const headingRef = useScrollReveal<HTMLDivElement>();
  const cardsRef = useStaggerReveal<HTMLDivElement>({ staggerMs: 120 });

  if (displayReviews.length === 0 && !isEditMode) {
    return null;
  }

  const renderStars = (rating: number = 5) => (
    <div className="flex items-center gap-0.5">
      {Array.from({ length: 5 }, (_, i) => (
        <StarIcon
          key={i}
          filled={i < rating}
          className={`w-4 h-4 ${i < rating ? 'text-amber-400' : 'text-[var(--vibe-color-border)]'}`}
        />
      ))}
    </div>
  );

  return (
    <section className="py-16 md:py-24 bg-[var(--vibe-color-primary)]">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div ref={headingRef as React.RefObject<HTMLDivElement>} className="reveal-fade-up text-center mb-12">
          <RichTextContent
            path="reviewscallout.title"
            label="Title"
            defaultValue='<p>What Residents Say About <span data-type="field-token" data-field="property_name"></span></p>'
            as="h2"
            className="font-heading text-3xl sm:text-4xl font-normal text-[var(--vibe-color-text-on-primary)] mb-4 uppercase tracking-wide"
          />

          <RichTextContent
            path="reviewscallout.subtitle"
            label="Subtitle"
            defaultValue='<p>Hear from the people who call <span data-type="field-token" data-field="property_name"></span> home.</p>'
            as="p"
            className="font-body text-lg text-[var(--vibe-color-text-on-primary)] max-w-2xl mx-auto"
          />
          <div className="w-16 h-1 mx-auto mt-6 rounded-full bg-[var(--vibe-color-text-on-primary)]/40" />
        </div>

        {displayReviews.length > 0 ? (
          <div ref={cardsRef as React.RefObject<HTMLDivElement>} className="flex flex-wrap justify-center gap-6 mb-12">
            {displayReviews.map((review: Review, index: number) => {
              const numericRating = review.rating != null ? parseInt(String(review.rating), 10) || 5 : 5;
              return (
                <div
                  key={index}
                  data-reveal
                  className="reveal-fade-up glow-trace-wrap bg-white p-8 shadow-sm flex flex-col relative w-full md:w-[calc(33.333%-1rem)]"
                >
                  <GlowTrace delay={0.3 + index * 0.25} />
                  <div className="mb-4">{renderStars(numericRating)}</div>

                  {hasValue(review.text) && (
                    <p className="font-body text-sm text-[var(--vibe-color-text)] mb-6 flex-grow leading-relaxed line-clamp-4">
                      &ldquo;{review.text}&rdquo;
                    </p>
                  )}

                  <div className="mt-auto pt-4 border-t border-[var(--vibe-color-border)]">
                    {hasValue(review.name) && (
                      <p className="font-body text-sm font-semibold text-[var(--vibe-color-text)]">
                        {abbreviateName(review.name)}
                      </p>
                    )}
                    {hasValue(review.date) && (
                      <p className="text-xs text-[var(--vibe-color-text-muted)] mt-1">
                        {review.date}
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-12 mb-12 text-white/60">
            <p className="font-body text-lg">Add reviews in the Multihub property profile for them to appear here</p>
          </div>
        )}

        <div
          className={
            showGoogleCta
              ? 'mx-auto grid w-full max-w-md grid-cols-1 justify-center gap-4 sm:w-fit sm:max-w-none sm:grid-cols-2'
              : 'flex justify-center'
          }
        >
          <EditableSlotGroup
            groupPath="reviewscallout.cta_link"
            definition={ctaLinkDef}
            override={slotGroupOverrides['reviewscallout.cta_link']}
          >
            <a
              href={ctaLink.url || '/reviews'}
              target={ctaLink._target || '_self'}
              className={ctaButtonClasses}
            >
              {ctaLink.label || 'Read All Reviews'}
            </a>
          </EditableSlotGroup>

          {showGoogleCta && (
            <EditableSlotGroup
              groupPath="reviewscallout.google_cta_link"
              definition={googleCtaLinkDef}
              override={slotGroupOverrides['reviewscallout.google_cta_link']}
            >
              <a
                href={googleReviewHref || '#'}
                target={googleCtaLink._target || '_blank'}
                rel="noopener noreferrer"
                className={ctaButtonClasses}
              >
                {googleCtaLink.label || 'Leave a Google Review'}
              </a>
            </EditableSlotGroup>
          )}
        </div>
      </div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'ReviewsCallout',
  displayName: 'Reviews Callout',
  tokens: {
    'reviewscallout.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue: '<p>What Residents Say About <span data-type="field-token" data-field="property_name"></span></p>',
    },
    'reviewscallout.subtitle': {
      label: 'Subtitle',
      type: 'richtext',
      defaultValue: '<p>Hear from the people who call <span data-type="field-token" data-field="property_name"></span> home.</p>',
    },
  },
  slotGroups: {
    'reviewscallout.cta_link': ctaLinkDef,
    'reviewscallout.google_cta_link': googleCtaLinkDef,
  },
  collectionBindings: {
    'reviewscallout.items': {
      label: 'Reviews',
      description: 'Featured resident reviews displayed in a callout grid',
      collection: 'reviews',
      defaultLimit: 3,
    },
  },
};

(ReviewsCallout as any).contentSchema = contentSchema;

export default ReviewsCallout;
